import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  Building2,
  Mail,
  User,
  Key,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuthStore } from "../stores/auth";

type Step = "info" | "creating" | "complete";

interface FormData {
  organizationName: string;
  email: string;
  contactName: string;
  plan: "starter" | "growth" | "business" | "enterprise";
}

const PLANS = [
  {
    id: "starter" as const,
    name: "Starter",
    price: "Free",
    description: "Perfect for testing and small projects",
    features: ["1,000 verifications/month", "100 credentials", "Community support"],
  },
  {
    id: "growth" as const,
    name: "Growth",
    price: "$99/mo",
    description: "For growing businesses",
    features: ["10,000 verifications/month", "1,000 credentials", "Email support", "Webhooks"],
  },
  {
    id: "business" as const,
    name: "Business",
    price: "$499/mo",
    description: "For established enterprises",
    features: ["100,000 verifications/month", "10,000 credentials", "Priority support", "SLA guarantee"],
    popular: true,
  },
  {
    id: "enterprise" as const,
    name: "Enterprise",
    price: "Custom",
    description: "For large organizations",
    features: ["Unlimited verifications", "Unlimited credentials", "Dedicated support", "Custom SLA", "SSO"],
  },
];

export function SignUp() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  
  const [step, setStep] = useState<Step>("info");
  const [formData, setFormData] = useState<FormData>({
    organizationName: "",
    email: "",
    contactName: "",
    plan: "starter",
  });
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlanSelect = (plan: FormData["plan"]) => {
    setFormData((prev) => ({ ...prev, plan }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setStep("creating");

    try {
      // Call API to create tenant
      const response = await api.createTenant({
        name: formData.organizationName,
        email: formData.email,
        plan: formData.plan.toUpperCase() as "STARTER" | "GROWTH" | "BUSINESS" | "ENTERPRISE",
      });

      if (response.error) {
        setError(response.error);
        setStep("info");
        setIsLoading(false);
        return;
      }

      if (response.data?.apiKey) {
        setGeneratedKey(response.data.apiKey);
        setStep("complete");
      } else {
        setError("Failed to generate API key. Please try again.");
        setStep("info");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
      setStep("info");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyKey = async () => {
    if (!generatedKey) return;
    
    try {
      await navigator.clipboard.writeText(generatedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = generatedKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleContinueToDashboard = async () => {
    if (!generatedKey) return;
    
    const success = await login(generatedKey);
    if (success) {
      navigate("/dashboard");
    } else {
      setError("Failed to authenticate with the new API key.");
    }
  };

  const isFormValid = formData.organizationName && formData.email && formData.contactName;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        <Link to="/signin" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          Already have an account? Sign in →
        </Link>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 pb-12">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-success-500 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Create Enterprise Account</h1>
          <p className="text-slate-600 mt-2">
            Set up your organization for privacy-preserving credential verification
          </p>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { id: "info", label: "Account Info" },
            { id: "creating", label: "Creating" },
            { id: "complete", label: "Complete" },
          ].map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step === s.id
                    ? "bg-primary-500 text-white"
                    : ["creating", "complete"].indexOf(step) > ["info", "creating", "complete"].indexOf(s.id)
                    ? "bg-success-500 text-white"
                    : "bg-slate-200 text-slate-500"
                  }`}
              >
                {["creating", "complete"].indexOf(step) > ["info", "creating", "complete"].indexOf(s.id) ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  index + 1
                )}
              </div>
              <span className={`ml-2 text-sm font-medium ${step === s.id ? "text-primary-600" : "text-slate-500"}`}>
                {s.label}
              </span>
              {index < 2 && <ArrowRight className="w-4 h-4 text-slate-300 mx-4" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Error message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Step: Info */}
          {step === "info" && (
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Organization info */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary-500" />
                  Organization Information
                </h2>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="organizationName" className="block text-sm font-medium text-slate-700 mb-2">
                      Organization Name *
                    </label>
                    <input
                      id="organizationName"
                      name="organizationName"
                      type="text"
                      required
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      placeholder="Acme Corporation"
                      className="block w-full px-4 py-3 border border-slate-300 rounded-lg 
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                               placeholder:text-slate-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <input
                        id="contactName"
                        name="contactName"
                        type="text"
                        required
                        value={formData.contactName}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className="block w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg 
                                 focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                 placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Business Email *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="w-5 h-5 text-slate-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      className="block w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg 
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                               placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>

              {/* Plan selection */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary-500" />
                  Choose Your Plan
                </h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  {PLANS.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => handlePlanSelect(plan.id)}
                      className={`relative text-left p-4 rounded-xl border-2 transition-all
                        ${formData.plan === plan.id
                          ? "border-primary-500 bg-primary-50"
                          : "border-slate-200 hover:border-primary-200 hover:bg-slate-50"
                        }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-2 right-4 px-2 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                          Popular
                        </span>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">{plan.name}</span>
                        <span className="text-lg font-bold text-primary-600">{plan.price}</span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3">{plan.description}</p>
                      <ul className="space-y-1">
                        {plan.features.slice(0, 3).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                            <CheckCircle2 className="w-3 h-3 text-success-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!isFormValid || isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 
                         bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300
                         text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-center text-sm text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="text-primary-600 hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          )}

          {/* Step: Creating */}
          {step === "creating" && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Creating your account...</h2>
              <p className="text-slate-600">Setting up your organization and generating your API key.</p>
            </div>
          )}

          {/* Step: Complete */}
          {step === "complete" && generatedKey && (
            <div className="space-y-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-100 mb-6">
                  <CheckCircle2 className="w-8 h-8 text-success-500" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Account created successfully!</h2>
                <p className="text-slate-600">
                  Welcome to ZKCN, <strong>{formData.organizationName}</strong>!
                </p>
              </div>

              {/* API Key display */}
              <div className="bg-slate-900 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary-400" />
                    <span className="text-sm font-medium text-slate-300">Your API Key</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="p-2 text-slate-400 hover:text-white transition-colors"
                      title={showKey ? "Hide key" : "Show key"}
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={handleCopyKey}
                      className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 
                               text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {keyCopied ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-success-400" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <code className="block text-lg font-mono text-primary-400 break-all">
                  {showKey ? generatedKey : generatedKey.slice(0, 14) + "•".repeat(20) + generatedKey.slice(-4)}
                </code>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Save this key securely</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This is the only time you'll see this API key. Store it in a secure location.
                    You can generate additional keys from your dashboard.
                  </p>
                </div>
              </div>

              {/* Continue button */}
              <button
                onClick={handleContinueToDashboard}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 
                         bg-primary-500 hover:bg-primary-600
                         text-white font-medium rounded-lg transition-colors"
              >
                Continue to Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
